import { type OasDocument3XExtended, pick } from '@ipfabric/utils';
import { createRoutesFromOas } from 'containers/App/Routes/oas/createRoutesFromOas';
import { ComponentGalleryPage } from 'pages/ComponentGallery/ComponentGalleryPage';
import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { DiscoveryConfigWizardPage } from 'app/features/DiscoverySettingsForms/pages/DiscoveryConfigWizardPage';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { pipe } from 'fp-ts/lib/function';
import { Eula } from 'pages/Auth/Eula/Eula';
import { Login } from 'pages/Auth/Login/Login';
import { Logout } from 'pages/Auth/Logout/Logout';
import { CanvasGraphPage } from 'pages/CanvasGraph/CanvasGraphPage';
import { ShareCanvasGraphPage } from 'pages/CanvasGraph/ShareCanvasGraphPage';
import { dashboardRoutes } from 'pages/Dashboard/Dashboard';
import { extensionsRoutes } from 'pages/Extensions/extensionsRoutes';
import { HomePage } from 'pages/HomePage/HomePage';
import { ApplicationDetailPage } from 'pages/Inventory/Applications/ApplicationDetail/ApplicationDetailPage';
import {
  APPLICATION_DETAIL_PATH,
  applicationDetailChildRoutes,
} from 'pages/Inventory/Applications/ApplicationDetail/applicationDetailTabs';
import { LicenseLimitExceededPage } from 'pages/LicenseLimitExceeded/LicenseLimitExceededPage';
import { managementRoutes } from 'pages/Management/Management';
import { meRoutes } from 'pages/Me/Me';
import { NotFoundPage } from 'pages/NotFoundPage/NotFoundPage';
import { reportsRoutes } from 'pages/Reports/Reports';
import SearchPage from 'pages/Search';
import { settingsRoutes } from 'pages/Settings/Settings';
import SnapshotManagementPage from 'pages/SnapshotManagement';
import { SnapshotManagementErrorsPage } from 'pages/SnapshotManagement/Errors/SnapshotManagementErrorsPage';
import { SnapshotManagementTasksPage } from 'pages/SnapshotManagement/Tasks/SnapshotManagementTasksPage';
import SupportSendDiagnosticsPage from 'pages/Support/SendDiagnostics';
import { SupportStatusPage } from 'pages/Support/Status/SupportStatusPage';
import { Techsupport as SupportTechsupportPage } from 'pages/Support/Techsupport/Techsupport';
import SupportVpnPage from 'pages/Support/Vpn';
import { UploadLicense } from 'pages/UploadLicense/UploadLicense';
import { LicenseGatedContent } from 'utils/pageGating/pageGating';
import { RestrictedPageAccess } from 'utils/permissions/RestrictedPageAccess';
import { createTableApiScopeRequirement } from 'utils/rbac/helpers/createTableApiScopeRequirement';
import { mergeRoutes } from './mergeRoutes';

export type CreateAllRoutesFunction = (
  oas: OasDocument3XExtended | null,
) => RoutesTreeItem[];

const wrapRouteElementWithRestrictions = (route: RoutesTreeItem): ReactNode =>
  route.requiredPermissions ||
  route.requiredApiScope ||
  route.requiredAuth ||
  route.requiredLicense ||
  route.requiredSnapshot ? (
    <RestrictedPageAccess
      {...pipe(
        route,
        pick([
          'requiredApiScope',
          'requiredAuth',
          'requiredLicense',
          'requiredPermissions',
          'requiredSnapshot',
        ]),
      )}
    >
      {route.element}
    </RestrictedPageAccess>
  ) : (
    route.element
  );

const wrapRouteElementsWithRestrictions = (
  routes: RoutesTreeItem[],
): RoutesTreeItem[] =>
  routes.map((route) => ({
    ...route,
    element: wrapRouteElementWithRestrictions(route),
    children: route.children
      ? wrapRouteElementsWithRestrictions(route.children)
      : undefined,
  }));

/**
 * Tree of all existing routes, not filtered by user access rights etc
 *
 * Since it imports (basically all) page elements, it is an instant generator of circular imports,
 * that's why we need this factory passed through context instead of just using it in a selector.
 *
 * Use `useGlobalStateContextSelectors({allRoutes: selectAllRoutes})` to get the resulting list.
 *
 * @internal
 */
export const createAllRoutes: CreateAllRoutesFunction = (oas) =>
  pipe(
    [
      ...(process.env.NODE_ENV === 'development'
        ? [
            {
              path: '/component-gallery',
              element: <ComponentGalleryPage />,
              children: [
                {
                  path: ':sectionKey',
                  element: <ComponentGalleryPage />,
                },
              ],
            },
          ]
        : []),
      {
        path: '/',
        requiredAuth: true,
        requiredLicense: true,
        element: <HomePage />,
      },
      {
        path: '/snapshot-management/errors',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['write'],
        element: <SnapshotManagementErrorsPage />,
      },
      {
        path: '/snapshot-management/tasks',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['write'],
        element: <SnapshotManagementTasksPage />,
      },
      {
        path: '/snapshot-management',
        requiredAuth: true,
        requiredLicense: true,
        element: <SnapshotManagementPage />,
        requiredApiScope: {
          and: [{ method: 'GET', path: '/snapshots' }],
        },
        children: [
          {
            path: ':snapshotId',
            element: <SnapshotManagementPage />,
          },
        ],
      },
      {
        path: '/snapshot-management/:snapshotId/settings',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['write'],
        element: <SnapshotManagementPage />,
      },
      {
        path: '/login',
        requiredLicense: false,
        element: <Login />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
      {
        path: '/eula',
        requiredLicense: true,
        element: <Eula />,
      },
      {
        path: '/wizard/:step',
        requiredAuth: true,
        requiredLicense: true,
        element: <DiscoveryConfigWizardPage />,
        requiredApiScope: { method: 'GET', path: '/settings/credentials' },
      },
      {
        path: '/wizard',
        element: <Navigate to='/wizard/1' />,
      },
      {
        path: '/upload-license',
        requiredAuth: true,
        element: <UploadLicense />,
      },
      {
        path: '/license-limit-exceeded',
        requiredAuth: true,
        requiredLicense: true,
        element: <LicenseLimitExceededPage />,
      },
      {
        path: '/dashboard',
        requiredAuth: true,
        requiredLicense: true,
        requiredApiScope: { method: 'GET', path: '/settings/dashboard' },
        requiredSnapshot: true,
        element: <Outlet />,
        children: dashboardRoutes,
      },
      {
        path: '/reports',
        requiredAuth: true,
        requiredLicense: true,
        requiredSnapshot: true,
        element: <Outlet />,
        children: reportsRoutes,
      },
      {
        path: '/inventory',
        requiredAuth: true,
        requiredLicense: true,
        requiredSnapshot: true,
        element: <Outlet />,
        children: [
          ...(oas ? createRoutesFromOas(oas, 'inventory') : []),
          ...(isFeatureFlagEnabled('ENABLE_AIM_APPLICATION_DETAIL')
            ? [
                {
                  path: APPLICATION_DETAIL_PATH,
                  requiredApiScope: createTableApiScopeRequirement(
                    '/inventory/applications',
                  ),
                  element: (
                    <LicenseGatedContent feature='aim'>
                      <ApplicationDetailPage />
                    </LicenseGatedContent>
                  ),
                  children: applicationDetailChildRoutes,
                } satisfies RoutesTreeItem,
              ]
            : []),
        ],
      },
      {
        path: '/diagrams/topology-tree',
        requiredAuth: true,
        requiredLicense: true,
        requiredApiScope: {
          and: [
            { method: 'GET', path: '/graphs/presets' },
            { method: 'GET', path: '/graphs/default-layout/network-viewer' },
            {
              method: 'GET',
              path: '/graphs/views',
            },
            {
              method: 'POST',
              path: '/tables/attributes/graphs',
            },
            { method: 'POST', path: '/graphs' },
          ],
        },
        requiredSnapshot: true,
        element: <CanvasGraphPage />,
      },
      {
        path: '/diagrams/pathlookup',
        requiredAuth: true,
        requiredLicense: true,
        requiredApiScope: {
          and: [
            { method: 'GET', path: '/graphs/presets' },
            { method: 'GET', path: '/graphs/path-lookup/advanced-options' },
            {
              method: 'GET',
              path: '/graphs/views',
            },
            {
              method: 'POST',
              path: '/tables/attributes/graphs',
            },
            { method: 'POST', path: '/graphs' },
          ],
        },
        requiredSnapshot: true,
        element: <CanvasGraphPage />,
      },
      {
        path: '/diagrams/share/:viewUrlId',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['read'],
        requiredApiScope: {
          and: [
            { method: 'GET', path: '/graphs/presets' },
            {
              or: [
                {
                  method: 'POST',
                  path: '/graphs/path-lookup/advanced-options',
                },
                {
                  method: 'GET',
                  path: '/graphs/default-layout/network-viewer',
                },
              ],
            },
            {
              method: 'GET',
              path: '/graphs/views',
            },
            {
              method: 'POST',
              path: '/tables/attributes/graphs',
            },
            { method: 'POST', path: '/graphs' },
            { method: 'GET', path: '/graphs/urls/:key' },
          ],
        },
        requiredSnapshot: true,
        element: <ShareCanvasGraphPage />,
      },
      {
        path: '/management',
        requiredAuth: true,
        requiredLicense: true,
        element: <Outlet />,
        children: managementRoutes,
      },
      {
        path: '/settings',
        requiredAuth: true,
        requiredLicense: true,
        element: <Outlet />,
        children: [
          ...mergeRoutes([
            settingsRoutes,
            oas ? createRoutesFromOas(oas, 'settings') : [],
          ]),
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
      {
        path: '/support/status',
        requiredAuth: true,
        requiredLicense: true,
        element: <SupportStatusPage />,
      },
      {
        path: '/support/diagnostics',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['read'],
        element: <SupportSendDiagnosticsPage />,
      },
      {
        path: '/support/vpn',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['read'],
        element: <SupportVpnPage />,
      },
      {
        path: '/support/techsupport',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['settings'],
        element: <SupportTechsupportPage />,
      },
      {
        path: '/me',
        requiredAuth: true,
        requiredLicense: true,
        requiredPermissions: ['read'],
        element: <Outlet />,
        children: meRoutes,
      },
      {
        path: '/technology',
        requiredAuth: true,
        requiredLicense: true,
        requiredSnapshot: true,
        element: <Outlet />,
        children: oas ? createRoutesFromOas(oas, 'technology') : [],
      },
      {
        path: '/search',
        requiredAuth: true,
        requiredLicense: true,
        element: <SearchPage />,
      },
      ...(isFeatureFlagEnabled('ENABLE_EXTENSIONS')
        ? [
            {
              path: '/extensions',
              requiredAuth: true,
              requiredLicense: true,
              requiredApiScope: {
                method: 'GET',
                path: '/extensions',
              },
              element: <Outlet />,
              children: extensionsRoutes,
            } satisfies RoutesTreeItem,
          ]
        : []),
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
    wrapRouteElementsWithRestrictions,
  );
